import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatError,
  isRecord,
  result,
  validation,
  validationError,
} from './shared.ts';

async function collect(): Promise<RawData> {
  const synth = window.speechSynthesis;
  let voices = synth.getVoices();
  if (voices.length == 0) {
    await Promise.race([
      new Promise<void>((resolve) => {
        synth.addEventListener('voiceschanged', () => resolve(), {
          once: true,
        });
      }),
      new Promise<void>((resolve) => {
        window.setTimeout(resolve, 1500);
      }),
    ]);
    voices = synth.getVoices();
  }
  return {
    voices: voices
      .filter((voice) => !voice.localService)
      .map((voice) => ({
        name: voice.name,
        lang: voice.lang,
        voiceURI: voice.voiceURI,
        default: voice.default,
        localService: voice.localService,
      })),
  };
}

function validateVoices(rawData: RawData): ValidationResult {
  const reasons: string[] = [];
  const voices = Array.isArray(rawData.voices)
    ? rawData.voices.filter(isRecord)
    : [];
  if (voices.some((voice) => !String(voice.name) || !String(voice.lang))) {
    reasons.push('A remote voice has an empty name or language');
  }
  if (voices.some((voice) => voice.localService != false)) {
    reasons.push('Remote voices contain a local voice');
  }
  if (voices.filter((voice) => voice.default == true).length > 1) {
    reasons.push('More than one remote voice is marked as default');
  }
  return validation(reasons);
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  const voices = Array.isArray(rawData.voices)
    ? rawData.voices.filter(isRecord)
    : [];
  const languages = new Set(
    voices.map((voice) => String(voice.lang)).filter(Boolean),
  ).size;
  return `voices ${voices.length} lang ${languages}`;
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return validateVoices(rawData);
}

export const remoteVoicesTest: TestDefinition = {
  id: 'remoteVoices',
  title: 'Remote voices',
  category: 'shared',
  format,
  run,
  validate,
};
